import {
  ActivityType,
  PumpSuspendReason,
} from "@tandemdiabetes/sugarmate-api-react-client"
import {
  BasalRateStoppedIcon,
  BasalRateSuspendedIcon,
  BasalRateZeroIcon,
  BGReadingFillIcon,
  BGReadingOutlineIcon,
  BolusFillIcon,
  BolusOutlineIcon,
  CGMReadingIcon,
  DeviceChangeIcon,
  ExerciseFillIcon,
  ExerciseOutlineIcon,
  FoodFillIcon,
  FoodOutlineIcon,
  InsulinFillIcon,
  InsulinOutlineIcon,
  LiquidFillIcon,
  LiquidOutlineIcon,
  MedicationFillIcon,
  MedicationOutlineIcon,
  NoteFillIcon,
  NoteOutlineIcon,
  QuestionMarkOutlineIcon,
} from "@tandemdiabetes/sugarmate-react-components"
import type {
  ComponentPropsWithoutRef,
  Ref,
} from "react"
import { forwardRef } from "react"

export interface Props extends ComponentPropsWithoutRef<"svg"> {
  activityType: ActivityType;
  suspendReason?: PumpSuspendReason;
  fillIcon?: boolean;
}

export const ActivityIcon = forwardRef<SVGElement, Props>(
  function ActivityIcon(props: Props, ref: Ref<SVGElement>) {
    const {
      activityType,
      suspendReason,
      fillIcon = false,
      ...svgProps
    } = props

    if (fillIcon) {
      switch (activityType) {
        case ActivityType.Bolus:
          return (
            <BolusFillIcon
              {...svgProps}
              ref={ref}
            />
          )
        case ActivityType.CGMSessionEnded:
          return (
            <CGMReadingIcon
              {...svgProps}
              ref={ref}
            />
          )
        case ActivityType.DeviceChange:
          return (
            <DeviceChangeIcon
              {...svgProps}
              ref={ref}
            />
          )
        case ActivityType.Exercise:
          return (
            <ExerciseFillIcon
              {...svgProps}
              ref={ref}
            />
          )
        case ActivityType.Food:
          return (
            <FoodFillIcon
              {...svgProps}
              ref={ref}
            />
          )
        case ActivityType.Glucose:
          return (
            <BGReadingFillIcon
              {...svgProps}
              ref={ref}
            />
          )
        case ActivityType.Liquid:
          return (
            <LiquidFillIcon
              {...svgProps}
              ref={ref}
            />
          )
        case ActivityType.Insulin:
          return (
            <InsulinFillIcon
              {...svgProps}
              ref={ref}
            />
          )
        case ActivityType.Medication:
          return (
            <MedicationFillIcon
              {...svgProps}
              ref={ref}
            />
          )
        case ActivityType.Note:
          return (
            <NoteFillIcon
              {...svgProps}
              ref={ref}
            />
          )
        case ActivityType.PumpSuspension:
          switch (suspendReason) {
            case PumpSuspendReason.BIQSuspend:
              return (
                <BasalRateSuspendedIcon
                  {...svgProps}
                  ref={ref}
                />
              )
            case PumpSuspendReason.CIQSuspend:
              return (
                <BasalRateZeroIcon
                  {...svgProps}
                  ref={ref}
                />
              )
            default:
              return (
                <BasalRateStoppedIcon
                  {...svgProps}
                  ref={ref}
                />
              )
          }
        default:
          return (
            <QuestionMarkOutlineIcon
              {...svgProps}
              ref={ref}
            />
          )
      }
    }

    switch (activityType) {
      case ActivityType.Bolus:
        return (
          <BolusOutlineIcon
            {...svgProps}
            ref={ref}
          />
        )
      case ActivityType.CGMSessionEnded:
        return (
          <CGMReadingIcon
            {...svgProps}
            ref={ref}
          />
        )
      case ActivityType.DeviceChange:
        return (
          <DeviceChangeIcon
            {...svgProps}
            ref={ref}
          />
        )
      case ActivityType.Exercise:
        return (
          <ExerciseOutlineIcon
            {...svgProps}
            ref={ref}
          />
        )
      case ActivityType.Food:
        return (
          <FoodOutlineIcon
            {...svgProps}
            ref={ref}
          />
        )
      case ActivityType.Glucose:
        return (
          <BGReadingOutlineIcon
            {...svgProps}
            ref={ref}
          />
        )
      case ActivityType.Liquid:
        return (
          <LiquidOutlineIcon
            {...svgProps}
            ref={ref}
          />
        )
      case ActivityType.Insulin:
        return (
          <InsulinOutlineIcon
            {...svgProps}
            ref={ref}
          />
        )
      case ActivityType.Medication:
        return (
          <MedicationOutlineIcon
            {...svgProps}
            ref={ref}
          />
        )
      case ActivityType.Note:
        return (
          <NoteOutlineIcon
            {...svgProps}
            ref={ref}
          />
        )
      case ActivityType.PumpSuspension:
        switch (suspendReason) {
          case PumpSuspendReason.BIQSuspend:
            return (
              <BasalRateSuspendedIcon
                {...svgProps}
                ref={ref}
              />
            )
          case PumpSuspendReason.CIQSuspend:
            return (
              <BasalRateZeroIcon
                {...svgProps}
                ref={ref}
              />
            )
          default:
            return (
              <BasalRateStoppedIcon
                {...svgProps}
                ref={ref}
              />
            )
        }
      default:
        return (
          <QuestionMarkOutlineIcon
            {...svgProps}
            ref={ref}
          />
        )
    }
  },
)
